import cx from 'classnames';
import { useCallback, useRef, useState } from 'react';
import LockedIcon from '~/icons/LockedIcon';
import Caret from './Caret';
import DropdownList from './DropdownList';
import { Container, Input, Label } from './RangeInput.styles';
import { RangeType } from '../constants';
import { type Errors } from '../hooks';

const updateActiveDescendant =
  (e: React.KeyboardEvent, rangeOptions: number[]) => (prevIndex: string) => {
    const { key } = e;
    let newIndex: string | undefined;

    if (key === 'ArrowDown' && Number(prevIndex) < rangeOptions.length - 1) {
      newIndex = (Number(prevIndex) + 1)?.toString();
    } else if (key === 'ArrowUp' && Number(prevIndex) > 0) {
      newIndex = (Number(prevIndex) - 1)?.toString();
    }

    return newIndex ?? prevIndex;
  };

type OwnProps = {
  disabled?: boolean;
  errors: Errors;
  filterKey: 'finance' | 'price';
  isValidInput: (type: RangeType, value: number) => boolean | string;
  maybeStartOpen?: boolean;
  rangeOptions: number[];
  rangeType: RangeType;
  updateErrors: (type: RangeType, value?: number) => void;
  updateFilters: (type: RangeType, value: number) => void;
  updateValues: (type: RangeType, value: number) => void;
  value?: string;
};

enum Labels {
  Min = 'Set Min',
  Max = 'Set Max'
}

const labelsMap = {
  min: Labels.Min,
  max: Labels.Max,
  [RangeType.DownPayment]: Labels.Max,
  [RangeType.MonthlyPayment]: Labels.Max
};

function RangeInput({
  disabled,
  errors,
  filterKey,
  isValidInput,
  maybeStartOpen = false,
  rangeOptions,
  rangeType,
  updateErrors,
  updateFilters,
  updateValues,
  value
}: OwnProps) {
  const [showDropdown, setShowDropdown] = useState(maybeStartOpen);
  const [activeDescendantIndex, setActiveDescendantIndex] =
    useState<string>('');
  const previousValueRef = useRef<string | undefined>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firedRequest = useRef(false);

  // these are primarily used for switching styles and functionality between mouse and keyboard
  const isMouseOverDropdownRef = useRef(false);
  const useMouseHoverStyleRef = useRef(false);

  const handleErrorAndResetValue = useCallback(
    (inputValue?: number) => {
      const stringInputValue = inputValue?.toString();
      if (stringInputValue !== '') {
        updateErrors(rangeType, inputValue);
        previousValueRef.current = stringInputValue;
      }
      firedRequest.current = false;
    },
    [updateErrors, rangeType]
  );

  const updateValue = e => {
    e.preventDefault();
    const validValuePattern = /^[0-9,]*$/;
    if (validValuePattern.test(e.target.value)) {
      updateValues(rangeType, Number(e.target.value.replace(/,/g, '')));
    }
  };

  const handleFocus = () => {
    updateErrors(rangeType);
    inputRef.current?.select();
    firedRequest.current = false;
    setShowDropdown(true);
  };

  const handleBlurAfterSelection = () => {
    if (isMouseOverDropdownRef.current) return;
    setActiveDescendantIndex('');
    useMouseHoverStyleRef.current = false;
    setShowDropdown(false);
    const numberValue = isNaN(Number(value)) ? 0 : Number(value);
    const valueHasChanged = value !== previousValueRef.current;

    if (firedRequest.current || !valueHasChanged) return;

    if (isValidInput(rangeType, numberValue)) {
      updateFilters(rangeType, numberValue);
      previousValueRef.current = value;
    } else {
      handleErrorAndResetValue(numberValue);
    }

    firedRequest.current = false;
  };

  const handleItemSelect = useCallback(
    (price: number, index: string) => {
      setActiveDescendantIndex(index);
      setShowDropdown(false);
      updateValues(rangeType, Number(price));

      const numberPrice = isNaN(Number(price)) ? 0 : Number(price);

      if (isValidInput(rangeType, numberPrice)) {
        updateFilters(rangeType, numberPrice);
        previousValueRef.current = price?.toString();
        firedRequest.current = true;
      } else {
        handleErrorAndResetValue(numberPrice);
      }
      inputRef.current?.blur();
    },
    [
      isValidInput,
      handleErrorAndResetValue,
      rangeType,
      updateFilters,
      updateValues
    ]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
      e.preventDefault();
      useMouseHoverStyleRef.current = false;
      const getNextIndex = updateActiveDescendant(e, rangeOptions);
      const valueToSet =
        activeDescendantIndex !== ''
          ? rangeOptions[activeDescendantIndex]
          : value;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setActiveDescendantIndex(getNextIndex);
      }

      if (e.key === 'Enter') {
        handleItemSelect(valueToSet, activeDescendantIndex?.toString());
      }
    } else {
      setActiveDescendantIndex('');
      updateErrors(rangeType);
      useMouseHoverStyleRef.current = true;
    }
  };

  return (
    <Container
      className={cx({
        'hover:border-grey-2 cursor-not-allowed': disabled,
        'border-red-2 hover:border-red-2': errors?.[rangeType],
        'border-white hover:border-white rounded-b-none shadow-[0px_0px_13px_0px_rgba(24,_53,_88,_0.10)] z-50':
          showDropdown
      })}
      data-analytics-id={`${filterKey}-range-input-${rangeType}-container`}
      onMouseEnter={() => (useMouseHoverStyleRef.current = true)}
      onMouseLeave={() => (useMouseHoverStyleRef.current = false)}
      ref={containerRef}
    >
      <div
        className={`${
          disabled ? 'bg-grey-1' : 'bg-white'
        } rounded w-full flex justify-between leading-5 pl-16 relative`}
      >
        <LockedIcon
          className={
            disabled
              ? 'absolute inset-y-0 left-16 m-auto block z-30 !stroke-grey-3'
              : 'hidden'
          }
        />
        <span
          className={disabled || !value ? 'hidden' : 'self-center text-blue-6'}
        >
          $
        </span>
        <Input
          aria-activedescendant={`${rangeType}-${filterKey}-${rangeOptions[activeDescendantIndex]}`}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-label={`set ${rangeType}`}
          autoComplete="off"
          data-analytics-id={`${filterKey}-range-input-${rangeType}`}
          disabled={disabled}
          id={`${filterKey}-${rangeType}`}
          list={`${filterKey}-${rangeType}-options`}
          maxLength={7}
          name={labelsMap[rangeType]}
          onBlur={handleBlurAfterSelection}
          onChange={updateValue}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          role="combobox"
          pattern="[0-9,]*"
          type="text"
          value={value ? Number(value).toLocaleString() : ''}
        />
        <Label
          data-analytics-id={`${filterKey}-range-input-label`}
          onClick={() => inputRef.current?.focus()}
          className={disabled || showDropdown || value ? 'hidden' : 'block'}
          htmlFor={`${filterKey}-${rangeType}`}
        >
          {labelsMap[rangeType]}
        </Label>
        <DropdownList
          activeDescendantIndex={activeDescendantIndex}
          filterKey={filterKey}
          handleItemSelect={handleItemSelect}
          isMouseOverDropdownRef={isMouseOverDropdownRef}
          rangeOptions={rangeOptions}
          rangeType={rangeType}
          showDropdown={showDropdown}
          useMouseHoverStyleRef={useMouseHoverStyleRef}
        />
        <Caret
          disabled={disabled}
          inputRef={inputRef}
          showDropdown={showDropdown}
        />
      </div>
    </Container>
  );
}

export default RangeInput;
